import { Component, effect, inject, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';
import { IconComponent } from '../icon/icon.component';
import { provideIcons, provideNgIconLoader, withCaching } from '@ng-icons/core';
import { heroMinusMini, heroPlusMini } from '@ng-icons/heroicons/mini';
import { HttpClient } from '@angular/common/http';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-slider',
  standalone: true,
  imports: [MatSliderModule, FormsModule, IconComponent, MatTooltipModule],
  templateUrl: './slider.component.html',
  styleUrl: './slider.component.scss',
  providers: [
    provideIcons({ heroMinusMini, heroPlusMini }),
    provideNgIconLoader((name) => {
      const http = inject(HttpClient);
      return http.get(`/assets/icons/${name}.svg`, { responseType: 'text' });
    }, withCaching()),
  ],
})
export class SliderComponent {
  currentSliderStartValue = input<number>();
  currentSliderEndValue = input<number>();
  // startValue and endValue is set from triangle or slider, depending which is enabled
  startValue = model.required<number>();
  endValue = model.required<number>();
  minStart = input.required<number>();
  maxEnd = input.required<number>();
  showAdditionalTooltips = input(true);
  valueChange = output();

  // slider values
  startSliderValue = signal<number>(0);
  endSliderValue = signal<number>(0);
  // triangle value
  currentValue = signal<number>(0);
  // triangle left transform value in percents, initially at the center
  triangleLeft = signal(50);
  isEnabled = signal<'slider' | 'triangle'>('slider');

  firstRun = true;

  constructor() {
    effect(() => {
      if (this.startValue() && this.endValue() && this.firstRun) {
        this.firstRun = false;
        this.startSliderValue.set(this.startValue());
        this.endSliderValue.set(this.endValue());
        this.currentValue.set(Math.round((this.startValue() + this.endValue()) / 2));
      }
    });

    effect(() => {
      this.startSliderValue.set(this.minStart());
      this.endSliderValue.set(this.maxEnd());
    })

    effect(() => {
      if (this.currentSliderStartValue()) {
        this.startSliderValue.set(this.currentSliderStartValue() as number);
      }

      if (this.currentSliderEndValue()) {
        this.endSliderValue.set(this.currentSliderEndValue() as number);
      }
    });
  }

  activate(type: 'slider' | 'triangle') {
    this.isEnabled.set(type);
  }

  onValueChange() {
    this.activate('slider');
    this.startValue.set(this.startSliderValue());
    this.endValue.set(this.endSliderValue());
    this.valueChange.emit();
  }

  onMinus() {
    if (this.startValue() === this.minStart()) {
      return;
    }

    this.startValue.update((value) => (value > this.minStart() ? value - 1 : value));
    this.startSliderValue.set(this.startValue());

    this.valueChange.emit();
  }

  onPlus() {
    if (this.endValue() === this.maxEnd()) {
      return;
    }

    this.endValue.update((value) => (value < this.maxEnd() ? value + 1 : value));
    this.endSliderValue.set(this.endValue());
    this.valueChange.emit();
  }

  startDrag(event: MouseEvent | TouchEvent, sliderWrapper: HTMLElement) {
    event.preventDefault();
    this.activate('triangle');

    const moveHandler = (e: MouseEvent | TouchEvent) => {
      this.onDragMove(e, sliderWrapper);
    };

    const endHandler = () => {
      window.removeEventListener('mousemove', moveHandler);
      window.removeEventListener('mouseup', endHandler);
      window.removeEventListener('touchmove', moveHandler);
      window.removeEventListener('touchend', endHandler);
      this.startValue.set(this.currentValue());
      this.endValue.set(this.currentValue());
      this.valueChange.emit();
    };

    window.addEventListener('mousemove', moveHandler);
    window.addEventListener('mouseup', endHandler);
    window.addEventListener('touchmove', moveHandler);
    window.addEventListener('touchend', endHandler);
  }

  onDragMove(event: MouseEvent | TouchEvent, sliderWrapper: HTMLElement) {
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;

    const rect = sliderWrapper.getBoundingClientRect();
    const relativeX = clientX - rect.left;
    const percent = Math.min(Math.max(relativeX / rect.width, 0), 1);

    const min = this.minStart();
    const max = this.maxEnd();
    const newValue = Math.round(min + percent * (max - min));
    this.triangleLeft.set(percent * 100);
    if (newValue !== this.currentValue()) {
      this.currentValue.set(newValue);
    }
  }
}
